import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, ShieldCheck } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLE_BADGE = {
  admin: "bg-blue-950/70 text-blue-300 border-blue-800",
  reviewer: "bg-amber-950/70 text-amber-300 border-amber-800",
  analyst: "bg-emerald-950/70 text-emerald-300 border-emerald-800",
  auditor: "bg-slate-800 text-slate-300 border-slate-700",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "analyst" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`Created ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "analyst" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const input =
    "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-blue-500";
  const lbl = "mb-1.5 block font-mono text-xs uppercase tracking-wider text-slate-400";

  return (
    <div className="max-w-4xl space-y-6" data-testid="users-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          Team &amp; Access
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Provision staff accounts. Accounts are admin-created only — there is no public sign-up.
        </p>
      </div>

      <form onSubmit={create} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-blue-400" />
          <h3 className="font-heading text-base font-semibold text-slate-200">Create staff account</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={lbl}>Full name</label>
            <input data-testid="user-name-input" className={input} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
          </div>
          <div>
            <label className={lbl}>Email</label>
            <input data-testid="user-email-input" type="email" className={input} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" />
          </div>
          <div>
            <label className={lbl}>Temporary password</label>
            <input data-testid="user-password-input" type="text" className={input} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" />
          </div>
          <div>
            <label className={lbl}>Role</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select" className={input}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="analyst">Analyst</SelectItem>
                <SelectItem value="reviewer">Reviewer (Compliance Lead)</SelectItem>
                <SelectItem value="auditor">Auditor (read-only)</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <button data-testid="create-user-btn" type="submit" disabled={saving}
          className="mt-5 flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Create account
        </button>
      </form>

      <div className="overflow-hidden rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 font-mono text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-800">
                <td className="px-4 py-3 text-slate-200">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[10px] ${ROLE_BADGE[u.role] || ROLE_BADGE.auditor}`}>
                    {u.role === "admin" && <ShieldCheck className="h-3 w-3" />}
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "admin" && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs text-red-300 transition-colors hover:border-red-700">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
